import { LOCAL_STORAGE_KEYS } from '@/constants'

import Vue from 'vue'
import http from '@/$plugins/http'

import base from '../base'
import IS from '../base/is'

export default {
  namespaced: true,
  state: base.createState(IS.state, {
    basket: {
      id: window.localStorage.getItem(LOCAL_STORAGE_KEYS.basket),
      itemCount: 0,
      lastAction: null
    },
    localConfigurations: []
  }),
  getters: base.createGetters(IS.getters, {
    get: state => state.basket,
    getId: state => window.localStorage.getItem(LOCAL_STORAGE_KEYS.basket),
    getItemCount: state => state.basket.itemCount,
    getLastAction: state => state.basket.lastAction || {},
    getConfiguration: state => state.basket.configuration || {},
    getValidationResult: state => state.basket.configurationValidationResult || {},
    getStepValidationResult: state => sectionName => ((state.basket.configurationValidationResult || {}).results || []).find(r => r.sectionName === sectionName) || { isValid: false, errors: {} },
    getSummary: state => state.basket.priceDetail || {},
    getLocalConfiguration: state => productId => state.localConfigurations.find(c => c.productId === productId)
  }),
  mutations: base.createMutations(IS.mutations, {
    set (state, { basket, merge, omitLastAction }) {
      if (omitLastAction) delete basket.lastAction
      state.basket = merge ? Object.assign(state.basket, basket) : basket
      window.localStorage.setItem(LOCAL_STORAGE_KEYS.basket, basket.id)
    },
    setLocalConfiguration (state, configuration = {}) {
      const updateIndex = state.localConfigurations.findIndex(c => c.productId === configuration.productId)
      const newIndex = state.localConfigurations.length
      const index = updateIndex >= 0 ? updateIndex : newIndex
      const base = state.localConfigurations[index] || {}

      Vue.set(state.localConfigurations, index, Object.assign(base, configuration))
    }
  }),
  actions: base.createActions(IS.actions, {
    getMinimal ({ state, getters, commit, dispatch, rootGetters }) {
      const isKey = 'ShoppingCart/getMinimal'
      commit('setLoading', { key: isKey, loading: true, initial: state.basket.basketState === undefined })

      return new Promise((resolve, reject) => {
        http({
          method: 'get',
          url: `/order/api/ShoppingCart/small${getters.getId ? `/${getters.getId}` : ''}?lang=${rootGetters['gui/getLanguage']}`
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: true })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setLoading', { key: isKey, loading: false })
          })
      })
    },
    get ({ state, getters, commit, dispatch, rootGetters }) {
      const isKey = 'ShoppingCart/get'
      commit('setLoading', { key: isKey, loading: true, initial: state.basket.basketState === undefined })

      return new Promise((resolve, reject) => {
        http({
          method: 'get',
          url: `/order/api/ShoppingCart${getters.getId ? `/${getters.getId}` : ''}?lang=${rootGetters['gui/getLanguage']}`
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setLoading', { key: isKey, loading: false })
          })
      })
    },
    update ({ state, getters, commit, dispatch, rootGetters }, basketConfiguration = {}) {
      const isKey = 'ShoppingCart/update'
      commit('setSending', { key: isKey, sending: true })

      return new Promise((resolve, reject) => {
        http({
          method: 'put',
          url: `/order/api/ShoppingCart/${getters.getId}?lang=${rootGetters['gui/getLanguage']}`,
          data: basketConfiguration
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setSending', { key: isKey, sending: false })
          })
      })
    },
    delete ({ state, getters, commit, dispatch, rootGetters }) {
      const isKey = 'ShoppingCart/delete'
      commit('setSending', { key: isKey, sending: true })

      return new Promise((resolve, reject) => {
        http({
          method: 'delete',
          url: `/order/api/ShoppingCart/${getters.getId}?lang=${rootGetters['gui/getLanguage']}`
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setSending', { key: isKey, sending: false })
          })
      })
    },
    addItem ({ state, getters, commit, dispatch, rootGetters }, { productId = '', productType = '', additionalConfigurations = null, quantity = 0, omitLastAction = false }) {
      const isKey = 'ShoppingCart/addItem'
      commit('setLoading', { key: isKey, loading: true, initial: state.basket.basketState === undefined })

      const localConfiguration = state.localConfigurations.find(c => c.productId === productId) || {}

      if (additionalConfigurations) {
        localConfiguration.additionalConfigurations = additionalConfigurations
      }

      return new Promise((resolve, reject) => {
        http({
          method: 'put',
          url: `/order/api/ShoppingCart/${getters.getId}/items/add/${quantity}/${productType}/${productId}?lang=${rootGetters['gui/getLanguage']}`,
          data: localConfiguration
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false, omitLastAction })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setLoading', { key: isKey, loading: false })
          })
      })
    },
    updateItem ({ state, getters, commit, dispatch, rootGetters }, { item = {}, omitLastAction = false }) {
      const isKey = 'ShoppingCart/updateItem'
      commit('setSending', { key: isKey, sending: true })

      const basketItem = state.basket.items.find(i => i.basketItemId === item.id) || {}

      return new Promise((resolve, reject) => {
        http({
          method: 'put',
          url: `/order/api/ShoppingCart/${getters.getId}/items/${item.id}/update/${item.quantity}?lang=${rootGetters['gui/getLanguage']}`,
          data: basketItem.configuration
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false, omitLastAction })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setSending', { key: isKey, sending: false })
          })
      })
    },
    deleteItem ({ state, getters, commit, dispatch, rootGetters }, { item = {}, omitLastAction = false }) {
      const isKey = 'ShoppingCart/addItem'
      commit('setSending', { key: isKey, sending: true })

      return new Promise((resolve, reject) => {
        http({
          method: 'delete',
          url: `/order/api/ShoppingCart/${getters.getId}/items/${item.id}/delete?lang=${rootGetters['gui/getLanguage']}`
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false, omitLastAction })
            resolve()
          })
          .catch(reject)
          .finally(() => {
            commit('setSending', { key: isKey, sending: false })
          })
      })
    }
  })
}
